import * as SentryVue from '@sentry/vue';
import { useRouter } from 'vue-router';
import type { App } from 'vue';

export const getSentryOptions = (app: App<Element>) => {
	const router = useRouter();

	return {
		app,
		dsn: import.meta.env.VITE_SENTRY_DSN,
		integrations: [
			SentryVue.httpClientIntegration(),
			SentryVue.browserTracingIntegration({
				router,
			}),
		],
		// This option is required for capturing headers and cookies.
		sendDefaultPii: true,
		// @ts-ignore
		release: __COMMIT_HASH__,
		// allowedUrls: ['api.primesurveys.com', 'api.primesurveysapp.dev'],

		// Set tracesSampleRate to 1.0 to capture 100%
		// of transactions for performance monitoring.
		// We recommend adjusting this value in production
		tracesSampleRate: 1,

		// Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
		tracePropagationTargets: [
			/^https:\/\/api\.primesurveys\.com/,
			/^https:\/\/api\.primesurveysapp\.dev/,
		],

		environment: import.meta.env.MODE,

		denyUrls: [
			/graph\.facebook\.com/i, // Facebook flakiness
			/connect\.facebook\.net\/en_US\/all\.js/i, // Facebook blocked
			/eatdifferent\.com\.woopra-ns\.com/i, // Woopra flakiness
			/static\.woopra\.com\/js\/woopra\.js/i,
			/.*facebook\.com.*/,
			/.*sentry\.io.*/,
			/.*google-analytics\.com.*/, // Google Analytics
			/.*googletagmanager\.com.*/, // Google Tag Manager (GTM)
			/.*hotjar\.com.*/, // Hotjar
			/.*hotjar-scripts\.com.*/, // Hotjar scripts
			/.*hotjar\.io.*/, // Hotjar-related assets
			/.*chrome-extension.*/, // Browser extensions
			/.*extensions\//, // More browser extension scripts
			/127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
			/webappstoolbarba\.texthelp\.com\//i,
			/metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
			/api\.fpjs\.io/i,
			/pagead2\.googlesyndication\.com/i,
			/prod\.rtymgt\.com/i,
			/.*redditstatic\.com/i,
		],

		// ===== Filter Errors Based on Context =====
		beforeSend(event, hint) {
			if (event.message && event.message.includes('Script error')) {
				return null; // Ignore generic script errors
			}

			if (hint?.originalException?.message?.includes('ResizeObserver')) {
				return null; // Ignore known harmless errors
			}

			return event;
		},

		// Ignore specific error messages if necessary
		ignoreErrors: [
			'ResizeObserver loop limit exceeded', // Browser bug
			'Non-Error promise rejection captured', // Often harmless
			'The quota has been exceeded', // Local storage limit
			/^SecurityError:.*/, // Security restrictions
			'Failed to load the JS script of the agent',
			// TODO: remove when other issues will be fixed
			// 'AxiosError: request canceled with timeout',
			// 'request canceled with timeout',
		],
	};
};
